<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useEmailPopover } from '@/composables/useEmailPopover'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import { genericSuccessToast, genericErrorToast } from '@/lib/toasts'
  import { type TEmail, EmailsService } from '@/types/generated'
  import {
    Eye,
    Pencil,
    X,
    ChevronDown,
    Search,
    CheckCircle,
    XCircle,
    FileText,
    FileQuestion,
    Copy,
    MoreVertical,
    ArrowUpRight,
    ArrowDownLeft,
    Plus,
    Reply,
    Download,
    Mail,
    ExternalLink,
    LucidePaperclip
  } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'

  const { toast } = useToast()
  const caseStore = useCaseStore()

  // Use the shared email popover state
  const emailPopover = useEmailPopover()

  // Email history section
  const emailHistory = ref<TEmail[]>([])
  const loadingEmailHistory = ref(false)
  const selectedEmailIndex = ref(0)
  const searchTerm = ref('')

  // Filtered emails based on search
  const filteredEmails = computed(() => {
    if (!searchTerm.value.trim()) return emailHistory.value

    const term = searchTerm.value.toLowerCase()
    return emailHistory.value.filter(
      (email) =>
        email.subject?.toLowerCase().includes(term) ||
        email.from_email.name?.toLowerCase().includes(term) ||
        email.from_email.email?.toLowerCase().includes(term) ||
        email.text?.toLowerCase().includes(term)
    )
  })

  // Email address section
  const emailAddressSectionOpen = ref(false)
  const emailAddress = caseStore.caseRecord?.org.inbound_email_address

  const { t } = useTranslation({
    en: {
      title: 'Email Communication',
      newEmail: 'New Email',
      inboundEmailAddress: 'Inbound Email Address',
      copySuccess: 'Email address copied to clipboard',
      copyError: 'Could not copy email address',
      inboundEmailDescription:
        'Forward new customer emails to this email address to attach them to the case.',
      copy: 'Copy',
      searchEmails: 'Search emails...',
      loadingEmails: 'Loading emails...',
      noResults: 'No Results',
      noResultsDescription: 'No emails were found containing "{search}".',
      resetSearch: 'Reset Search',
      noEmails: 'No Emails',
      noEmailsDescription: 'Emails connected to this case will appear here.',
      createEmail: 'Create New Email',
      unknown: 'Unknown',
      noSubject: 'No Subject',
      noMessage: 'No Message',
      of: 'of',
      to: 'To:',
      noContent: 'No Content Available',
      noContentDescription: 'This email contains no text or HTML content.',
      attachments: 'Attachments:',
      emailPdf: 'Email PDF',
      emptyStateTitle: 'No Emails Available',
      emptyStateDescription:
        'There is no email correspondence for this case yet. Create a new email to start communicating.',
      reply: 'Reply'
    },
    de: {
      title: 'E-Mail Kommunikation',
      newEmail: 'Neue E-Mail',
      inboundEmailAddress: 'Fallspezifische E-Mail Adresse',
      copySuccess: 'E-Mail Adresse wurde in die Zwischenablage kopiert',
      copyError: 'E-Mail Adresse konnte nicht kopiert werden',
      inboundEmailDescription:
        'Leiten Sie neue E-Mails von Kunden an diese E-Mail-Adresse weiter, um sie dem Fall anzuhängen.',
      copy: 'Kopieren',
      searchEmails: 'E-Mails durchsuchen...',
      loadingEmails: 'E-Mails werden geladen...',
      noResults: 'Keine Treffer',
      noResultsDescription: 'Es wurden keine E-Mails gefunden, die "{search}" enthalten.',
      resetSearch: 'Suche zurücksetzen',
      noEmails: 'Keine E-Mails vorhanden',
      noEmailsDescription: 'Hier werden E-Mails angezeigt, die mit diesem Fall verbunden sind.',
      createEmail: 'Neue E-Mail erstellen',
      unknown: 'Unbekannt',
      noSubject: 'Kein Betreff',
      noMessage: 'Keine Nachricht',
      of: 'von',
      to: 'An:',
      noContent: 'Keine Inhalte verfügbar',
      noContentDescription: 'Diese E-Mail enthält keinen Text oder HTML-Inhalt.',
      attachments: 'Anhänge:',
      emailPdf: 'Email-PDF',
      emptyStateTitle: 'Keine E-Mails vorhanden',
      emptyStateDescription:
        'Zu diesem Fall gibt es noch keine E-Mail-Korrespondenz. Erstellen Sie eine neue E-Mail, um mit der Kommunikation zu beginnen.',
      reply: 'Antworten'
    }
  })

  const fetchEmailHistory = async () => {
    try {
      loadingEmailHistory.value = true
      const response = await EmailsService.emailsList({
        _case: caseStore.caseRecord?.uuid
      })
      emailHistory.value = response.results
    } catch (error) {
      genericErrorToast()
    } finally {
      loadingEmailHistory.value = false
    }
  }

  const copyEmailAddress = async () => {
    try {
      await navigator.clipboard.writeText(emailAddress || '')
      toast({
        title: 'Erfolg',
        description: t('copySuccess'),
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: 'Fehler',
        description: t('copyError'),
        variant: 'destructive'
      })
    }
  }

  const handleDownloadPdf = (email: TEmail) => {
    if (email.pdf_url) {
      window.open(email.pdf_url, '_blank')
    } else {
      toast({
        title: 'Fehler',
        description: 'PDF konnte nicht gefunden werden',
        variant: 'destructive'
      })
    }
  }

  // Open new email popover
  const openNewEmail = () => {
    emailPopover.resetPosition()
    emailPopover.openPopover(null)
  }

  // Open reply email popover
  const openReplyEmail = () => {
    if (emailHistory.value.length && selectedEmailIndex.value >= 0) {
      const email = emailHistory.value[selectedEmailIndex.value]
      emailPopover.resetPosition()
      emailPopover.openPopover({
        subject: `Re: ${email.subject || ''}`,
        to: email.from_email.email
      })
    }
  }

  // Handle email sent event
  const handleEmailSent = async () => {
    await fetchEmailHistory()
  }

  // Select email
  const selectEmail = (index: number) => {
    selectedEmailIndex.value = index
  }

  // Function to generate consistent colors based on email
  const getEmailColors = (email: string) => {
    // Generate a simple hash of the email
    const hash = email.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc)
    }, 0)

    // Define color pairs (background, text) - using Tailwind color palette
    const colorPairs = [
      ['bg-blue-100', 'text-blue-600'],
      ['bg-green-100', 'text-green-600'],
      ['bg-amber-100', 'text-amber-600'],
      ['bg-purple-100', 'text-purple-600'],
      ['bg-rose-100', 'text-rose-600'],
      ['bg-cyan-100', 'text-cyan-600']
    ]

    // Use the hash to consistently select a color pair
    const index = Math.abs(hash) % colorPairs.length
    return colorPairs[index]
  }

  // Computed property for email colors
  const getEmailAvatarColors = computed(() => (email: string) => {
    return getEmailColors(email || '')
  })

  onMounted(async () => {
    await fetchEmailHistory()

    // Set up a listener for email sent events from the global popover
    const unsubscribe = emailPopover.onEmailSent(handleEmailSent)

    onUnmounted(() => {
      // Clean up the event listener when component is unmounted
      unsubscribe()
    })
  })
</script>

<template>
  <div class="mt-5 min-h-screen w-full space-y-6">
    <!-- Main Container with rounded corners and shadow -->
    <div class="overflow-hidden rounded-3xl bg-white shadow-lg">
      <!-- Header Section -->
      <div
        class="flex flex-col border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:border-b-0
          sm:px-6 sm:py-4"
      >
        <h1 class="mb-3 text-lg font-medium text-gray-900 sm:mb-0 sm:text-xl">{{ t('title') }}</h1>

        <div class="flex items-center space-x-3">
          <div class="relative">
            <Button
              variant="secondary"
              class="group flex items-center gap-2 px-4 py-2 transition-all"
              @click="emailAddressSectionOpen = !emailAddressSectionOpen"
            >
              <Mail class="h-5 w-5 text-gray-700 group-hover:text-gray-700" />
              {{ t('inboundEmailAddress') }}
              <ChevronDown
                class="h-4 w-4 text-gray-500 transition-transform duration-200 group-hover:text-gray-700"
                :class="{ 'rotate-180': emailAddressSectionOpen }"
              />
            </Button>

            <!-- Email Address Dropdown -->
            <div
              v-if="emailAddressSectionOpen"
              class="absolute right-0 z-50 mt-2 w-[32rem] overflow-hidden rounded-lg border border-gray-200 bg-white
                shadow-lg"
            >
              <div class="border-b border-gray-100 bg-gray-50 px-4 py-3">
                <h3 class="text-sm font-medium text-gray-900">Fallspezifische E-Mail Adresse</h3>
              </div>
              <div class="p-4">
                <p class="mb-3 text-sm text-gray-600">
                  {{ t('inboundEmailDescription') }}
                </p>
                <div class="relative">
                  <input
                    v-model="emailAddress"
                    placeholder="E-Mail Adresse"
                    class="w-full rounded-md border border-gray-300 bg-gray-50 px-4 py-2.5 pr-24 text-sm
                      focus:border-purple-400 focus:ring-purple-300"
                    readonly
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    class="absolute right-1 top-1/2 inline-flex -translate-y-1/2 items-center gap-2 px-3 py-1.5
                      transition-colors"
                    @click="copyEmailAddress"
                  >
                    <Copy class="h-4 w-4" />
                    {{ t('copy') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <Button
            variant="default"
            class="flex items-center gap-2"
            @click="openNewEmail"
          >
            <Plus class="h-4 w-4 sm:h-5 sm:w-5" />
            {{ t('newEmail') }}
          </Button>
        </div>
      </div>

      <!-- Content Area with Search and Panels -->
      <div class="flex flex-col gap-4 p-2 sm:p-4 lg:flex-row">
        <!-- Left Side (Search + Email List) -->
        <div class="order-2 w-full lg:order-1 lg:w-2/5">
          <!-- Search Bar -->
          <div class="mb-4">
            <div class="relative w-full">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search class="h-4 w-4 text-gray-400" />
              </div>
              <input
                v-model="searchTerm"
                type="text"
                :placeholder="t('searchEmails')"
                class="w-full rounded-xl border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-purple-500
                  focus:ring-purple-500"
              />
            </div>
          </div>

          <!-- Email List Panel -->
          <div class="rounded-2xl border border-gray-200 bg-white">
            <div
              class="scrollbar-thin scrollbar-track-rounded-2xl scrollbar-track-gray-100 scrollbar-thumb-gray-300
                hover:scrollbar-thumb-gray-400 mx-1 my-1 h-[250px] overflow-y-auto rounded-xl pr-1 sm:h-[350px]
                lg:h-[calc(100vh-230px)]"
            >
              <!-- Loading State -->
              <div
                v-if="loadingEmailHistory"
                class="flex h-full items-center justify-center py-12"
              >
                <div class="flex flex-col items-center">
                  <div
                    class="border-3 h-10 w-10 animate-spin rounded-full border-gray-200 border-t-purple-600"
                  ></div>
                  <p class="mt-3 text-sm text-gray-500">{{ t('loadingEmails') }}</p>
                </div>
              </div>

              <!-- No Results from Search -->
              <div
                v-else-if="emailHistory.length > 0 && filteredEmails.length === 0"
                class="flex h-full flex-col items-center justify-center py-8 text-center sm:py-16"
              >
                <div class="mb-3 rounded-full bg-gray-100 p-4">
                  <Search class="h-6 w-6 text-gray-400 sm:h-8 sm:w-8" />
                </div>
                <p class="font-medium text-gray-700">{{ t('noResults') }}</p>
                <p class="mt-1 max-w-sm text-sm text-gray-500">
                  {{ t('noResultsDescription', { search: searchTerm }) }}
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  class="mt-4 flex items-center gap-2 text-sm"
                  @click="searchTerm = ''"
                >
                  <X class="h-4 w-4" />
                  {{ t('resetSearch') }}
                </Button>
              </div>

              <!-- Empty State -->
              <div
                v-else-if="emailHistory.length === 0"
                class="flex h-full flex-col items-center justify-center py-8 text-center sm:py-16"
              >
                <div class="mb-3 rounded-full bg-gray-100 p-4">
                  <FileQuestion class="h-6 w-6 text-gray-400 sm:h-8 sm:w-8" />
                </div>
                <p class="font-medium text-gray-700">{{ t('noEmails') }}</p>
                <p class="mt-1 max-w-sm text-sm text-gray-500">
                  {{ t('noEmailsDescription') }}
                </p>
                <Button
                  variant="secondary"
                  class="mt-4 flex items-center gap-2 text-sm"
                  @click="openNewEmail"
                >
                  <Plus class="h-4 w-4" />
                  {{ t('createEmail') }}
                </Button>
              </div>

              <!-- Email List -->
              <div v-else-if="filteredEmails.length > 0">
                <div
                  v-for="(email, index) in filteredEmails"
                  :key="email.uuid"
                  class="cursor-pointer border-b border-gray-200"
                  :class="{ 'bg-gray-50': selectedEmailIndex === index }"
                  @click="selectEmail(index)"
                >
                  <div class="relative p-3 sm:p-4">
                    <div class="flex gap-2 sm:gap-3">
                      <!-- Avatar -->
                      <div class="flex-shrink-0">
                        <div
                          class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600 sm:h-12 sm:w-12"
                          :class="
                            getEmailAvatarColors(email.from_email.email || '')[0] +
                            '' +
                            getEmailAvatarColors(email.from_email.email || '')[1]
                          "
                        >
                          <span class="text-sm font-medium sm:text-base">
                            {{ email.from_email.name?.[0] || email.from_email.email?.[0] || '?' }}
                          </span>
                        </div>
                      </div>

                      <!-- Content -->
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center justify-between">
                          <p class="truncate text-sm font-medium text-gray-900 sm:text-base">
                            {{ email.from_email.name || t('unknown') }}
                          </p>
                          <div class="flex items-center">
                            <p class="text-xs font-light text-gray-500 sm:text-sm">
                              {{
                                new Date(email.sent_at)
                                  .toLocaleString('de-DE', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                  .includes(
                                    new Date().toLocaleDateString('de-DE', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })
                                  )
                                  ? new Date(email.sent_at).toLocaleString('de-DE', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) + ' Uhr'
                                  : new Date(email.sent_at).toLocaleDateString('de-DE', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })
                              }}
                            </p>
                          </div>
                        </div>
                        <p class="truncate text-xs text-gray-500 sm:text-sm">
                          {{ email.from_email.email }}
                        </p>
                        <p class="mt-2 text-sm font-medium text-gray-800 sm:text-base">
                          {{ email.subject || t('noSubject') }}
                        </p>
                        <p class="line-clamp-1 text-xs text-gray-600 sm:text-sm">
                          {{ email.text || t('noMessage') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel (Email Content) -->
        <div
          v-if="filteredEmails.length > 0"
          class="order-1 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white lg:order-2 lg:w-3/5"
        >
          <!-- Email Header -->
          <div class="border-b p-3 sm:p-4">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-center gap-3">
                <div class="flex-shrink-0">
                  <div
                    class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600 sm:h-12 sm:w-12"
                    :class="
                      getEmailAvatarColors(
                        filteredEmails[selectedEmailIndex].from_email.email || ''
                      )[0] +
                      '' +
                      getEmailAvatarColors(
                        filteredEmails[selectedEmailIndex].from_email.email || ''
                      )[1]
                    "
                  >
                    <span class="text-base font-medium sm:text-lg">
                      {{
                        filteredEmails[selectedEmailIndex].from_email.name?.[0] ||
                        filteredEmails[selectedEmailIndex].from_email.email?.[0] ||
                        '?'
                      }}
                    </span>
                  </div>
                </div>

                <div class="flex flex-col">
                  <p class="text-sm font-medium text-gray-900 sm:text-base">
                    {{ filteredEmails[selectedEmailIndex].from_email.name || t('unknown') }}
                  </p>
                  <p class="text-xs text-gray-500 sm:text-sm">
                    {{ filteredEmails[selectedEmailIndex].from_email.email }}
                  </p>
                </div>
              </div>

              <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <p class="text-xs text-gray-500 sm:text-sm">
                  {{
                    new Date(filteredEmails[selectedEmailIndex].sent_at).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  }}
                </p>

                <div class="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    class="flex items-center gap-1 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                    @click="openReplyEmail"
                  >
                    <Reply class="h-4 w-4 sm:h-5 sm:w-5" />
                    {{ t('reply') }}
                  </Button>

                  <Button
                    v-if="filteredEmails[selectedEmailIndex].pdf_url"
                    variant="secondary"
                    class="flex items-center gap-1 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
                    @click="handleDownloadPdf(filteredEmails[selectedEmailIndex])"
                  >
                    <Download class="h-4 w-4 sm:h-5 sm:w-5" />
                    {{ t('emailPdf') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <!-- Email Body -->
          <div
            class="px-3 sm:px-6"
            style="min-height: 240px; max-height: calc(100vh - 320px); overflow-y: auto"
          >
            <div class="py-2 text-sm font-medium text-gray-900 sm:text-base">
              {{ filteredEmails[selectedEmailIndex].subject || t('noSubject') }}
            </div>
            <!-- If there's HTML content, render it safely -->
            <div
              v-if="filteredEmails[selectedEmailIndex].html"
              class="text-sm sm:text-base"
              v-html="filteredEmails[selectedEmailIndex].html"
            ></div>

            <!-- Otherwise show text content if available -->
            <div v-else-if="filteredEmails[selectedEmailIndex].text">
              <p
                v-for="(paragraph, i) in filteredEmails[selectedEmailIndex].text
                  .split('\n')
                  .filter((p) => p.trim())"
                :key="i"
                class="mb-4 text-sm text-gray-800 sm:text-base"
              >
                {{ paragraph }}
              </p>
            </div>

            <!-- No content available -->
            <div
              v-else
              class="flex flex-col items-center justify-center py-8 text-center sm:py-12"
            >
              <div class="mb-3 rounded-full bg-gray-100 p-4">
                <FileText class="h-6 w-6 text-gray-400 sm:h-8 sm:w-8" />
              </div>
              <p class="font-medium text-gray-700">{{ t('noContent') }}</p>
              <p class="mt-1 max-w-sm text-sm text-gray-500">
                {{ t('noContentDescription') }}
              </p>
            </div>

            <!-- Attachments section -->
            <!-- <div
              v-if="filteredEmails[selectedEmailIndex].attachments?.length"
              class="mt-8 border-t border-gray-200 pt-4"
            >
              <p class="mb-2 font-medium text-gray-700">{{ t('attachments') }}</p>
              <div class="flex flex-wrap gap-2">
                <a
                  v-for="(attachment, i) in filteredEmails[selectedEmailIndex].attachments"
                  :key="i"
                  :href="attachment.url"
                  target="_blank"
                  class="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700
                    hover:bg-gray-100 sm:text-sm"
                >
                  <FileText class="h-4 w-4 text-gray-500" />
                  {{ attachment.name }}
                  <ExternalLink class="h-3 w-3 text-gray-500" />
                </a>
              </div>
            </div> -->
          </div>
        </div>

        <!-- Empty State for Email Content -->
        <div
          v-else
          class="order-1 flex w-full flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white
            p-4 sm:p-6 lg:order-2 lg:w-3/5"
        >
          <div class="rounded-full bg-gray-100 p-4 sm:p-6">
            <Mail class="h-8 w-8 text-gray-400 sm:h-10 sm:w-10" />
          </div>
          <h3 class="mt-4 text-base font-medium text-gray-700 sm:text-lg">
            {{ t('emptyStateTitle') }}
          </h3>
          <p class="mt-2 max-w-md text-center text-xs text-gray-500 sm:text-sm">
            {{ t('emptyStateDescription') }}
          </p>
          <Button
            variant="default"
            class="mt-4 flex items-center gap-2 sm:mt-6"
            @click="openNewEmail"
          >
            <Plus class="h-4 w-4" />
            {{ t('createEmail') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
